/**
 * ثوابت وحدة العملاء.
 *
 * المفاتيح بحروف كبيرة لتطابق أنواع enum في قاعدة البيانات حرفيًا، والعناوين
 * العربية هنا لا في الواجهة، حتى تُعرض التسمية نفسها في الشاشات وفي المستندات
 * المطبوعة وفي التقارير المصدَّرة دون أن تتفرّق النصوص بين ثلاثة أماكن.
 */
import type { Labelled } from './common.js';

/* ==========================================================================
   العملاء المحتملون
   ========================================================================== */

export const LEAD_STATUSES = [
  'NEW',
  'CONTACTED',
  'WORKING',
  'QUALIFIED',
  'CONVERTED',
  'UNQUALIFIED',
  'JUNK',
] as const;
export type LeadStatus = (typeof LEAD_STATUSES)[number];

export interface LeadStatusDef extends Labelled<LeadStatus> {
  color: string;
  order: number;
  /** حالة نهائية لا يُتابَع بعدها العميل. */
  terminal: boolean;
}

export const LEAD_STATUS_DEFS: readonly LeadStatusDef[] = [
  { key: 'NEW', label: 'جديد', kind: 'neutral', color: '#9BA3AE', order: 1, terminal: false },
  { key: 'CONTACTED', label: 'تم التواصل', kind: 'info', color: '#3672EA', order: 2, terminal: false },
  { key: 'WORKING', label: 'قيد المتابعة', kind: 'warning', color: '#EDDA2E', order: 3, terminal: false },
  { key: 'QUALIFIED', label: 'مؤهّل', kind: 'success', color: '#51A521', order: 4, terminal: false },
  { key: 'CONVERTED', label: 'تم التحويل', kind: 'brand', color: '#1D46A6', order: 5, terminal: true },
  { key: 'UNQUALIFIED', label: 'غير مؤهّل', kind: 'danger', color: '#DC2626', order: 6, terminal: true },
  { key: 'JUNK', label: 'مستبعد', kind: 'neutral', color: '#6F7784', order: 7, terminal: true },
] as const;

export const LEAD_STATUS_MAP = Object.fromEntries(
  LEAD_STATUS_DEFS.map((d) => [d.key, d]),
) as Readonly<Record<LeadStatus, LeadStatusDef>>;

/** الحالات التي ما زال العميل فيها قيد العمل. */
export const OPEN_LEAD_STATUSES: readonly LeadStatus[] = LEAD_STATUS_DEFS.filter(
  (d) => !d.terminal,
).map((d) => d.key);

export const LEAD_RATINGS = ['HOT', 'WARM', 'COLD'] as const;
export type LeadRating = (typeof LEAD_RATINGS)[number];

export const LEAD_RATING_DEFS: readonly Labelled<LeadRating>[] = [
  { key: 'HOT', label: 'أولوية عالية', kind: 'danger' },
  { key: 'WARM', label: 'أولوية متوسطة', kind: 'warning' },
  { key: 'COLD', label: 'أولوية منخفضة', kind: 'info' },
] as const;

export const LEAD_RATING_MAP = Object.fromEntries(
  LEAD_RATING_DEFS.map((d) => [d.key, d]),
) as Readonly<Record<LeadRating, Labelled<LeadRating>>>;

/* ==========================================================================
   الحسابات
   ========================================================================== */

export const ACCOUNT_KINDS = ['INDIVIDUAL', 'COMPANY'] as const;
export type AccountKind = (typeof ACCOUNT_KINDS)[number];

export const ACCOUNT_KIND_DEFS: readonly Labelled<AccountKind>[] = [
  { key: 'INDIVIDUAL', label: 'حساب فردي', kind: 'info' },
  { key: 'COMPANY', label: 'حساب شركة', kind: 'brand' },
] as const;

export const ACCOUNT_KIND_MAP = Object.fromEntries(
  ACCOUNT_KIND_DEFS.map((d) => [d.key, d]),
) as Readonly<Record<AccountKind, Labelled<AccountKind>>>;

export const COMPANY_SIZES = ['MICRO', 'SMALL', 'MEDIUM', 'LARGE'] as const;
export type CompanySize = (typeof COMPANY_SIZES)[number];

export const COMPANY_SIZE_LABELS: Readonly<Record<CompanySize, string>> = {
  MICRO: 'أقل من عشرة موظفين',
  SMALL: 'من عشرة إلى خمسين',
  MEDIUM: 'من واحد وخمسين إلى مئتين وخمسين',
  LARGE: 'أكثر من مئتين وخمسين',
};

/* ==========================================================================
   الصفقات
   ========================================================================== */

export const DEAL_STAGES = [
  'CONTACT',
  'QUALIFICATION',
  'PROPOSAL',
  'NEGOTIATION',
  'CLOSING',
] as const;
export type DealStage = (typeof DEAL_STAGES)[number];

export interface DealStageDef {
  key: DealStage;
  label: string;
  short: string;
  order: number;
  /** الاحتمالية الافتراضية للإغلاق، تُستخدم في التوقعات وتقبل التعديل يدويًا. */
  probability: number;
  color: string;
  hint: string;
}

export const DEAL_STAGE_DEFS: readonly DealStageDef[] = [
  {
    key: 'CONTACT',
    label: 'مرحلة التواصل',
    short: 'تواصل',
    order: 1,
    probability: 10,
    color: '#9BA3AE',
    hint: 'تم التواصل المبدئي مع العميل وتسجيل بياناته.',
  },
  {
    key: 'QUALIFICATION',
    label: 'مرحلة التأهيل',
    short: 'تأهيل',
    order: 2,
    probability: 30,
    color: '#5596FF',
    hint: 'تم التأكد من احتياج العميل وقدرته على الشراء.',
  },
  {
    key: 'PROPOSAL',
    label: 'مرحلة العرض',
    short: 'عرض',
    order: 3,
    probability: 60,
    color: '#3672EA',
    hint: 'تم إرسال عرض السعر وجارٍ انتظار الرد.',
  },
  {
    key: 'NEGOTIATION',
    label: 'مرحلة التفاوض',
    short: 'تفاوض',
    order: 4,
    probability: 80,
    color: '#EDDA2E',
    hint: 'جارٍ التفاوض على الشروط والأسعار.',
  },
  {
    key: 'CLOSING',
    label: 'مرحلة الإغلاق',
    short: 'إغلاق',
    order: 5,
    probability: 90,
    color: '#DDBC10',
    hint: 'الصفقة في مرحلة القرار النهائي.',
  },
] as const;

export const DEAL_STAGE_MAP = Object.fromEntries(
  DEAL_STAGE_DEFS.map((d) => [d.key, d]),
) as Readonly<Record<DealStage, DealStageDef>>;

export const DEAL_RESULTS = ['OPEN', 'WON', 'LOST'] as const;
export type DealResult = (typeof DEAL_RESULTS)[number];

export const DEAL_RESULT_DEFS: readonly Labelled<DealResult>[] = [
  { key: 'OPEN', label: 'قيد العمل', kind: 'info' },
  { key: 'WON', label: 'صفقة ناجحة', kind: 'success' },
  { key: 'LOST', label: 'صفقة خاسرة', kind: 'danger' },
] as const;

export const DEAL_RESULT_MAP = Object.fromEntries(
  DEAL_RESULT_DEFS.map((d) => [d.key, d]),
) as Readonly<Record<DealResult, Labelled<DealResult>>>;

/* ==========================================================================
   الأنشطة
   ========================================================================== */

export const ACTIVITY_TYPES = ['TASK', 'MEETING', 'CALL', 'MAIL', 'NOTE'] as const;
export type ActivityType = (typeof ACTIVITY_TYPES)[number];

export interface ActivityTypeDef {
  key: ActivityType;
  label: string;
  plural: string;
  color: string;
}

export const ACTIVITY_TYPE_DEFS: readonly ActivityTypeDef[] = [
  { key: 'TASK', label: 'مهمة', plural: 'المهام', color: '#3672EA' },
  { key: 'MEETING', label: 'اجتماع', plural: 'الاجتماعات', color: '#51A521' },
  { key: 'CALL', label: 'مكالمة', plural: 'المكالمات', color: '#5596FF' },
  { key: 'MAIL', label: 'رسالة', plural: 'الرسائل', color: '#BF930B' },
  /*
   * الملاحظة نشاطٌ لا حقل.
   *
   * كانت تُكتب في عمود `notes` على العميل: نصٌّ واحد يُستبدل بكل تعديل، فلا
   * يُعرف من كتب ماذا ولا متى، وتضيع السابقة عند كتابة التالية. وكنشاطٍ
   * تصير مؤرَّخة ومنسوبة وتدخل الخطّ الزمني مع بقية ما جرى.
   *
   * وهي وحدها من الأنواع لا موعد لها ولا إنجاز: تُسجَّل وقد وقعت.
   */
  { key: 'NOTE', label: 'ملاحظة', plural: 'الملاحظات', color: '#8791A1' },
] as const;

export const ACTIVITY_TYPE_MAP = Object.fromEntries(
  ACTIVITY_TYPE_DEFS.map((d) => [d.key, d]),
) as Readonly<Record<ActivityType, ActivityTypeDef>>;

export const ACTIVITY_STATUSES = ['PLANNED', 'DONE', 'CANCELLED'] as const;
export type ActivityStatus = (typeof ACTIVITY_STATUSES)[number];

/**
 * قنوات الرسالة — نشاط «رسالة» يحدّد قناته، وكلٌّ تظهر عند أهلها:
 * البريد في «البريد الوارد»، وقنوات ميتا وSMS في «قنوات ميتا» ضمن
 * «الرسائل القادمة» — فيعرف المرسِل ماذا يُبعث ومن أين.
 */
export const MESSAGE_CHANNELS = ['EMAIL', 'WHATSAPP', 'MESSENGER', 'INSTAGRAM', 'SMS'] as const;
export type MessageChannel = (typeof MESSAGE_CHANNELS)[number];

export interface MessageChannelDef {
  key: MessageChannel;
  label: string;
  /** أين يجدها منفّذها. */
  home: string;
}

export const MESSAGE_CHANNEL_DEFS: readonly MessageChannelDef[] = [
  { key: 'EMAIL', label: 'بريد إلكتروني', home: 'البريد الوارد' },
  { key: 'WHATSAPP', label: 'واتساب', home: 'قنوات ميتا' },
  { key: 'MESSENGER', label: 'ماسنجر', home: 'قنوات ميتا' },
  { key: 'INSTAGRAM', label: 'إنستغرام', home: 'قنوات ميتا' },
  { key: 'SMS', label: 'SMS', home: 'من جهاز الموظف' },
];

export const MESSAGE_CHANNEL_MAP = Object.fromEntries(
  MESSAGE_CHANNEL_DEFS.map((d) => [d.key, d]),
) as Readonly<Record<MessageChannel, MessageChannelDef>>;

export const ACTIVITY_STATUS_DEFS: readonly Labelled<ActivityStatus>[] = [
  { key: 'PLANNED', label: 'مجدول', kind: 'info' },
  { key: 'DONE', label: 'مكتمل', kind: 'success' },
  { key: 'CANCELLED', label: 'ملغي', kind: 'danger' },
] as const;

export const ACTIVITY_STATUS_MAP = Object.fromEntries(
  ACTIVITY_STATUS_DEFS.map((d) => [d.key, d]),
) as Readonly<Record<ActivityStatus, Labelled<ActivityStatus>>>;

export const PRIORITIES = ['HIGH', 'NORMAL', 'LOW'] as const;
export type Priority = (typeof PRIORITIES)[number];

export const PRIORITY_DEFS: readonly Labelled<Priority>[] = [
  { key: 'HIGH', label: 'عالية', kind: 'danger' },
  { key: 'NORMAL', label: 'عادية', kind: 'info' },
  { key: 'LOW', label: 'منخفضة', kind: 'neutral' },
] as const;

export const PRIORITY_MAP = Object.fromEntries(
  PRIORITY_DEFS.map((d) => [d.key, d]),
) as Readonly<Record<Priority, Labelled<Priority>>>;

export const CALL_DIRECTIONS = ['OUTBOUND', 'INBOUND'] as const;
export type CallDirection = (typeof CALL_DIRECTIONS)[number];

export const CALL_DIRECTION_LABELS: Readonly<Record<CallDirection, string>> = {
  OUTBOUND: 'صادرة',
  INBOUND: 'واردة',
};

export const MEETING_PROVIDERS = ['ONSITE', 'JITSI', 'ZOOM', 'MEET', 'TEAMS', 'PHONE'] as const;
export type MeetingProvider = (typeof MEETING_PROVIDERS)[number];

export const MEETING_PROVIDER_LABELS: Readonly<Record<MeetingProvider, string>> = {
  ONSITE: 'حضوري',
  JITSI: 'اجتماع داخلي (Jitsi)',
  ZOOM: 'Zoom',
  MEET: 'Google Meet',
  TEAMS: 'Microsoft Teams',
  PHONE: 'هاتفي',
};

/* ==========================================================================
   قوائم القيم الافتراضية
   تُنسخ إلى جدول المستأجر عند إنشائه، ثم يعدّلها كما يشاء من الإعدادات.
   ========================================================================== */

export const LOOKUP_KINDS = [
  'leadSource',
  'industry',
  'lostReason',
  'callOutcome',
  'paymentMethod',
  'department',
  'position',
] as const;
export type LookupKind = (typeof LOOKUP_KINDS)[number];

export const DEFAULT_LOOKUPS: Readonly<Record<LookupKind, readonly string[]>> = {
  leadSource: [
    'حملة فيسبوك',
    'حملة إنستجرام',
    'إعلانات جوجل',
    'لينكدإن',
    'الموقع الإلكتروني',
    'توصية عميل',
    'زيارة ميدانية',
    'معرض تجاري',
    'مكالمة واردة',
    'بريد إلكتروني',
    'شريك تجاري',
    'أخرى',
  ],
  industry: [
    'تجارة التجزئة',
    'العقارات',
    'التعليم',
    'الرعاية الصحية',
    'السياحة والسفر',
    'المطاعم والضيافة',
    'الصناعة',
    'الخدمات المالية',
    'التقنية والبرمجيات',
    'الإعلام والإعلان',
    'المقاولات',
    'أخرى',
  ],
  lostReason: [
    'السعر مرتفع',
    'اختار منافسًا',
    'تأجّل القرار',
    'لا توجد ميزانية',
    'لا يوجد احتياج فعلي',
    'انقطع التواصل',
    'أخرى',
  ],
  callOutcome: [
    'تم الرد والاتفاق على خطوة تالية',
    'تم الرد دون قرار',
    'طلب معاودة الاتصال لاحقًا',
    'لم يتم الرد',
    'رقم غير صحيح',
    'غير مهتم',
  ],
  paymentMethod: ['نقدًا', 'تحويل بنكي', 'شيك', 'محفظة إلكترونية', 'إنستاباي', 'بطاقة ائتمان'],
  department: [
    'الإدارة',
    'المبيعات',
    'التسويق',
    'الإنتاج',
    'خدمة العملاء',
    'الحسابات',
    'الموارد البشرية',
  ],
  position: [
    'مدير عام',
    'مدير مبيعات',
    'مندوب مبيعات',
    'أخصائي تسويق',
    'محاسب',
    'أخصائي موارد بشرية',
    'خدمة عملاء',
  ],
};

/* ==========================================================================
   دلاء الأنشطة
   ========================================================================== */

/**
 * التقسيم الذي يعمل به الموظف فعلًا.
 *
 * الحالة والنوع تصنيفان صحيحان لكنهما ليسا سؤال الصباح. سؤال الصباح هو:
 * ما الذي فات موعده؟ وما المطلوب اليوم؟ وما القادم؟ — وهو تقسيم بالزمن لا
 * بالنوع، ولا يُشتقّ من عمود واحد بل من الحالة والموعد معًا.
 *
 * موضعه هنا لا في الواجهة لأن الخادم يعدّه ويصفّي به: قاعدةٌ في الواجهة
 * وحدها تعني أن الرقم المعروض قد لا يطابق ما تفتحه البطاقة بنقرة.
 */
export const ACTIVITY_BUCKETS = ['overdue', 'today', 'upcoming', 'done'] as const;
export type ActivityBucket = (typeof ACTIVITY_BUCKETS)[number];

export interface ActivityBucketDef {
  key: ActivityBucket;
  label: string;
  color: string;
  hint: string;
}

export const ACTIVITY_BUCKET_DEFS: readonly ActivityBucketDef[] = [
  {
    key: 'overdue',
    label: 'فات موعدها',
    color: '#DC2626',
    hint: 'مجدولة ومضى موعدها ولم تُكتمل.',
  },
  {
    key: 'today',
    label: 'اليوم',
    color: '#3672EA',
    hint: 'مجدولة اليوم ولم يحن موعدها بعد أو حان الآن.',
  },
  {
    key: 'upcoming',
    label: 'قادمة',
    color: '#51A521',
    hint: 'مجدولة بعد اليوم.',
  },
  {
    key: 'done',
    label: 'أُنجزت',
    color: '#8791A1',
    hint: 'مكتملة خلال الثلاثين يومًا الماضية.',
  },
] as const;

export const ACTIVITY_BUCKET_MAP = Object.fromEntries(
  ACTIVITY_BUCKET_DEFS.map((d) => [d.key, d]),
) as Readonly<Record<ActivityBucket, ActivityBucketDef>>;

/* ==========================================================================
   الخطّ الزمني
   ========================================================================== */

/**
 * حدثٌ واحد في تاريخ سجل.
 *
 * الخطّ الزمني يجمع ثلاثة مصادر لم تكن تُقرأ معًا: الأنشطة، وتغيّرات الحقول
 * من سجل التدقيق، والمرفقات. كلٌّ منها كان يُعرض في مكانه، فمن أراد أن يعرف
 * ماذا جرى مع هذا العميل يفتح ثلاث شاشات ويرتّبها بذهنه.
 *
 * والقيم تُعاد **خامًا** — `from` و`to` مفتاحان لا نصّان مترجمان. الترجمة في
 * الواجهة لأنها تملك ما لا يملكه الخادم: دليل الفريق الذي يحوّل معرّف
 * المستخدم إلى اسم وصورة. وإرسال نصّ جاهز كان سيثبّت الصياغة في قاعدة
 * البيانات فلا تتغيّر بتغيّر الكتالوج.
 */
export const TIMELINE_KINDS = ['created', 'activity', 'field', 'attachment'] as const;
export type TimelineKind = (typeof TIMELINE_KINDS)[number];

export interface TimelineEvent {
  id: string;
  /** وقت وقوعه بصيغة ISO. */
  at: string;
  kind: TimelineKind;
  /**
   * التخصيص داخل النوع.
   *
   *   activity    نوع النشاط: CALL أو MEETING أو TASK أو MAIL.
   *   field       اسم الحقل: ownerId أو status أو rating.
   *   attachment  نوع الملف.
   */
  subtype: string | null;
  title: string;
  detail: string | null;
  /** لنشاط المكالمة: نتيجتها كما اختارها الموظف عند الإنهاء. */
  outcome?: string | null;
  /** لنشاط المكالمة: معرّف مرفق تسجيلها الصوتي — يُسمع من الملف مباشرة. */
  recordingId?: string | null;
  /** لنشاط الاجتماع: رابط تسجيل الفيديو — يُشغَّل من الملف مباشرة. */
  recordingUrl?: string | null;
  actorId: string | null;
  actorName: string | null;
  /** القيمة السابقة، للحقول وحدها. */
  from: string | null;
  /** القيمة الجديدة، للحقول وحدها. */
  to: string | null;
}

export interface TimelineKindDef {
  key: TimelineKind;
  label: string;
}

/** مرشّحات الخطّ الزمني. */
export const TIMELINE_KIND_DEFS: readonly TimelineKindDef[] = [
  { key: 'activity', label: 'الأنشطة' },
  { key: 'field', label: 'التغييرات' },
  { key: 'attachment', label: 'المرفقات' },
  { key: 'created', label: 'الإنشاء' },
] as const;

/**
 * الحقول التي يُعرض تغيّرها في الخطّ الزمني.
 *
 * قائمة مغلقة عمدًا: سجل التدقيق يحفظ كل حقل في جسم الطلب، وعرضها جميعًا
 * يُغرق الخطّ بأسطر «غُيّر العنوان» و«غُيّرت الملاحظات» فيُدفن فيها ما يهم.
 */
export const TIMELINE_FIELDS: Readonly<Record<string, string>> = {
  ownerId: 'المسؤول',
  status: 'الحالة',
  rating: 'التصنيف',
};

/* ==========================================================================
   دلاء العملاء
   ========================================================================== */

/**
 * التقسيم الذي يبني عليه المدير قراره.
 *
 * «نشط» و«معطَّل» حالة مخزَّنة، أما «بلا نشاط» و«بلا مسؤول» فسؤالان لا
 * يجيب عنهما عمود واحد — وهما أهمّ ما يُسأل: أي عميل يُهمَل، وأي عميل لا
 * أحد مسؤول عنه.
 *
 * و«عالي القيمة» غائب عمدًا: تعريفه قرار عمل لا هندسة — أهو الأعلى فوترةً
 * أم تحصيلًا؟ وفوق أي حدّ، أم أعلى عشرة؟ اختيارُ أحدها بلا سؤال يضع رقمًا
 * على الشاشة يبني عليه المدير قرارًا وهو لا يعرف كيف حُسب.
 */
export const ACCOUNT_BUCKETS = ['active', 'recent', 'stale', 'unassigned'] as const;
export type AccountBucket = (typeof ACCOUNT_BUCKETS)[number];

/** حدّ الإهمال بالأيام — بعده يُعدّ العميل بلا نشاط. */
export const ACCOUNT_STALE_DAYS = 60;

/** حدّ «أُضيف حديثًا» بالأيام. */
export const ACCOUNT_RECENT_DAYS = 30;

export interface AccountBucketDef {
  key: AccountBucket;
  label: string;
  color: string;
  hint: string;
}

export const ACCOUNT_BUCKET_DEFS: readonly AccountBucketDef[] = [
  { key: 'active', label: 'نشطون', color: '#51A521', hint: 'عملاء لم يُعطَّلوا.' },
  {
    key: 'recent',
    label: 'أُضيفوا حديثًا',
    color: '#3672EA',
    hint: `أُضيفوا خلال ${ACCOUNT_RECENT_DAYS} يومًا.`,
  },
  {
    key: 'stale',
    label: 'بلا نشاط',
    color: '#BF930B',
    hint: `لا نشاط معهم منذ ${ACCOUNT_STALE_DAYS} يومًا أو أكثر.`,
  },
  {
    key: 'unassigned',
    label: 'بلا مسؤول',
    color: '#DC2626',
    hint: 'لم يُسنَد إليهم أحد — لا يتابعهم أحد بالضرورة.',
  },
] as const;

export const ACCOUNT_BUCKET_MAP = Object.fromEntries(
  ACCOUNT_BUCKET_DEFS.map((d) => [d.key, d]),
) as Readonly<Record<AccountBucket, AccountBucketDef>>;

/* ==========================================================================
   دلاء جهات الاتصال
   ========================================================================== */

/**
 * كم يومًا تُعدّ جهة الاتصال «مضافةً حديثًا».
 */
export const CONTACT_RECENT_DAYS = 30;

/**
 * دلاء جهات الاتصال.
 *
 * وهي تجيب سؤال من يبني حملةً أو يوزّع متابعة، لا سؤال «كم عندي»:
 * الرئيسية هي من يُخاطَب باسم العميل، ومن بلا بريد لا تصله رسالة مهما
 * أُضيف إلى قائمة، ومن بلا مسؤول لا يتابعه أحد.
 *
 * والدلاء تتقاطع عمدًا: الجهة الرئيسية قد تكون بلا بريد وبلا مسؤول معًا.
 */
export const CONTACT_BUCKETS = ['primary', 'reachable', 'noEmail', 'unassigned', 'recent'] as const;
export type ContactBucket = (typeof CONTACT_BUCKETS)[number];

export interface ContactBucketDef {
  key: ContactBucket;
  label: string;
  color: string;
  hint: string;
}

export const CONTACT_BUCKET_DEFS: readonly ContactBucketDef[] = [
  {
    key: 'primary',
    label: 'الجهات الرئيسية',
    color: '#3672EA',
    hint: 'من يُخاطَب باسم العميل ويظهر على المستندات.',
  },
  {
    key: 'reachable',
    label: 'يمكن الوصول إليهم',
    color: '#51A521',
    hint: 'لهم هاتف أو بريد — يصلح لحملة أو متابعة.',
  },
  {
    key: 'noEmail',
    label: 'بلا بريد',
    color: '#BF930B',
    hint: 'لا تصلهم رسالة مهما أُضيفوا إلى قائمة.',
  },
  {
    key: 'unassigned',
    label: 'بلا مسؤول',
    color: '#8791A1',
    hint: 'لا مسؤول لها ولا لعميلها — لا يتابعها أحد.',
  },
  {
    key: 'recent',
    label: 'أُضيفوا حديثًا',
    color: '#5596FF',
    hint: `أُضيفوا خلال ${CONTACT_RECENT_DAYS} يومًا.`,
  },
] as const;

export const CONTACT_BUCKET_MAP = Object.fromEntries(
  CONTACT_BUCKET_DEFS.map((d) => [d.key, d]),
) as Readonly<Record<ContactBucket, ContactBucketDef>>;

/* ==========================================================================
   دلاء العملاء المحتملين
   ========================================================================== */

/**
 * التقسيم الذي يعمل به المندوب صباحًا.
 *
 * الحالات السبع تصنيفٌ صحيح لمرحلة العميل، وليست سؤال الصباح. السؤال هو:
 * ما الجديد؟ وأيّها ساخن؟ وأيّها حان موعد متابعته؟ ومن لا مسؤول له؟ — وهي
 * أسئلة تجمع بين حقول لا يجيب عنها عمود واحد.
 *
 * و«يحتاج متابعة» يُعرَّف بموعد المتابعة الذي حلّ أو فات، لا بمدة انقطاع.
 * السبب أن مدة الانقطاع معرَّفة أصلًا في غرفة العمليات بعتبة يضبطها
 * المسؤول (`STALE_LEAD` و`UNTOUCHED_LEAD`)، وتعريفٌ ثانٍ لها هنا يعني
 * رقمين يفترقان: البطاقة تقول خمسة والغرفة تقول ثمانية، ولا أحد يعرف
 * أيهما يصدّق.
 */
export const LEAD_BUCKETS = ['today', 'hot', 'followup', 'converted', 'unassigned'] as const;
export type LeadBucket = (typeof LEAD_BUCKETS)[number];

export interface LeadBucketDef {
  key: LeadBucket;
  label: string;
  color: string;
  hint: string;
}

export const LEAD_BUCKET_DEFS: readonly LeadBucketDef[] = [
  { key: 'today', label: 'جدد اليوم', color: '#3672EA', hint: 'أُضيفوا منذ بداية اليوم.' },
  {
    key: 'hot',
    label: 'أولوية عالية',
    color: '#DC2626',
    hint: 'أولويتهم عالية وما زالوا مفتوحين.',
  },
  {
    key: 'followup',
    label: 'يحتاجون متابعة',
    color: '#BF930B',
    hint: 'حلّ موعد متابعتهم المحدَّد أو فات.',
  },
  {
    key: 'converted',
    label: 'تحوّلوا',
    color: '#51A521',
    hint: 'أصبحوا عملاء.',
  },
  {
    key: 'unassigned',
    label: 'بلا مسؤول',
    color: '#8791A1',
    hint: 'مفتوحون ولم يُسنَد إليهم أحد — لا يتابعهم أحد بالضرورة.',
  },
] as const;

export const LEAD_BUCKET_MAP = Object.fromEntries(
  LEAD_BUCKET_DEFS.map((d) => [d.key, d]),
) as Readonly<Record<LeadBucket, LeadBucketDef>>;
